import React, { useMemo, useState } from 'react';
import { View, SectionList, Text } from 'react-native-web';
import { cloneDeep } from 'lodash';
import { useRoute } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';

// Custom Components
import { NavBar } from '../../components';

// Utils
import { checkDate } from '../../modules/converters';

// Configs
import GLOBAL from '../../configs/config_global';
import { STATE_KEYS, useGlobalState } from '../../configs/config_state';

// Navigation
import * as RootNavigation from '../../navigation/RootNavigation';
import RouteKeys from '../../navigation/RouteKeys';

export function PastCatering() {
    const {
        pastCateringCategories = {},
        renderSectionHeader = () => <View/>,
        renderSectionItem = () => <View/>,
        onHiredArrUpdated = () => {},
        onPastDataUpdated = () => {}
    } = useRoute()?.params || {};

    // region Global State
    const [pastCateringListObj] = useGlobalState(STATE_KEYS.PAST_CATERING_LIST_OBJ);
    // endregion

    // region Local State
    const [collapsedSections, setCollapsedSections] = useState(['upcoming', 'notHired']);
    // endregion

    // region Function - On Catering Hired
    const onCateringHired = (hiredData, wasHired = true) => {
        let cateringID = hiredData.id,
            tempCalendarListObj = cloneDeep(pastCateringListObj),
            hiredRequestIndex = tempCalendarListObj['upcoming'].data.findIndex((item) => item.id === cateringID);

        // Search if catering already in either Not Hired or Hired list
        let cateringIndexInHired = tempCalendarListObj['hired'].data.findIndex((item) => item.id === cateringID),
            cateringIndexInNotHired = tempCalendarListObj['notHired'].data.findIndex((item) => item.id === cateringID),
            tempObj = Object.assign({}, hiredData, { timeObj: checkDate(hiredData, true) });

        // Remove the catering from either Not Hired or Hired list first
        // because sometimes the portal will change a lead from Hired -> Not Hired or vice-versa
        if (cateringIndexInHired !== -1) tempCalendarListObj['hired'].data.splice(cateringIndexInHired, 1);
        if (cateringIndexInNotHired !== -1) tempCalendarListObj['notHired'].data.splice(cateringIndexInNotHired, 1);

        // Add catering to the list again
        tempCalendarListObj[wasHired ? 'hired' : 'notHired'].data.push(tempObj);
        tempCalendarListObj[wasHired ? 'hired' : 'notHired'].data.sort((a, b) => (a.timeObj.startTime - b.timeObj.startTime));

        // Remove this catering from Accepted list if existed there
        if (hiredRequestIndex !== -1) tempCalendarListObj['upcoming'].data.splice(hiredRequestIndex, 1);

        // Callback to parent
        onPastDataUpdated(tempCalendarListObj);
    }
    // endregion

    // region Function - On Catering Pressed
    const _onCateringPressed = (item) => {
        RootNavigation.push(RouteKeys.CATERING_DETAILS, {
            cateringData: item,
            onHired: (hiredData, wasHired) => onCateringHired(hiredData, wasHired),
            onFeedbackSubmitted: (updatedCatering) => {
                const tempArr = pastCateringListObj['hired'].data;
                const cateringIndex = tempArr.findIndex((item) => item.id === updatedCatering.id);
                if (cateringIndex > -1) {
                    tempArr.splice(cateringIndex, 1, updatedCatering);
                    onHiredArrUpdated(tempArr);
                }
            }
        })
    }
    // endregion

    // region Render Mark Hired Reminder
    const renderMarkHiredReminder = (count = 0) => (
        <View
            style={{
                width: '100%', paddingHorizontal: 16, paddingVertical: 16, backgroundColor: '#FFA600',
                flexDirection: 'row', alignItems: 'center'
            }}>
            <FeatherIcon name={'info'} size={26} color={GLOBAL.COLOR.BASIC.BLACK_TWO} />
            <Text style={{ fontSize: 16, fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginHorizontal: 16, lineHeight: 20, flex: 1 }}>
                    You have {count} past accepted lead{count > 1 ? 's' : ''}. Accurately mark them as Hired or Not Hired to unlock new leads.
            </Text>
        </View>
    )
    // endregion

    // region MAIN RENDER
    const cateringSections = useMemo(() => (
        Object.keys(pastCateringCategories).map((key) => {
            const sectionData = pastCateringListObj[key];
            const color = pastCateringCategories[key].color, title = pastCateringCategories[key].title;
            return({
                key,
                title,
                data: collapsedSections.includes(key) ? [] : sectionData.data.map((item) => Object.assign(cloneDeep(item), { sectionColor: color, sectionTitle: title })),
                totalCount: sectionData.data.length
            });
        })
    ), [pastCateringListObj, pastCateringCategories, collapsedSections]);

    const acceptedCount = pastCateringListObj['upcoming']?.data?.length || 0;

    return(
        <View style={{ width: '100%', flex: 1, backgroundColor: GLOBAL.COLOR.MAIN_BACKGROUND }}>
            {/* Nav Bar */}
            <NavBar
                title={'Past Catering Events'}
                leftButtonIconName={'keyboard-arrow-left'}
                leftButtonIconSize={40}
                leftButtonOnPress={() => RootNavigation.goBack()}
            />

            {/* Mark Hired Reminder */}
            {acceptedCount > 0 && renderMarkHiredReminder(acceptedCount)}

            {/* List */}
            <SectionList
                style={{ flex: 1, width: '100%', backgroundColor: GLOBAL.COLOR.BACKGROUND_GREY }}
                showsVerticalScrollIndicator={true}
                contentContainerStyle={{ paddingBottom: 24 + 56, paddingHorizontal: '15%' }}
                sections={cateringSections}
                renderItem={({ item }) => renderSectionItem(item, false, () => _onCateringPressed(item))}
                renderSectionHeader={({ section }) => renderSectionHeader(section.title, section.totalCount, () => {
                    setCollapsedSections(collapsedSections.includes(section.key) ? collapsedSections.filter((key) => key !== section.key) : collapsedSections.concat(section.key));
                })}
                keyExtractor={(item) => item.id?.toString()}
                removeClippedSubviews={true}
                maxToRenderPerBatch={20}
                ItemSeparatorComponent={() => <View style={{ height: 1, width: '100%', backgroundColor: GLOBAL.COLOR.DIVIDER }}/>}
            />
        </View>
    );
    // endregion
}
